import { useCallback, useState } from "react";
import GlobalFilter from "./GlobalFilter";
import ReactToPrint from "react-to-print";
import { Button, Col, Row, DropdownButton, Dropdown } from "react-bootstrap";
import { ZTooltip } from "components/ZTooltip";
import { ZIcon } from "components/ZIcon";
import { ZIconType } from "helpers/utility/iconMap";
import { ZButton } from "components/ZButton";

export function Toolbar({ dataTable, isSearchable, disableExport, selectedRowCount, searchColumns }: any) {
    const reactToPrintContent = useCallback(() => { return dataTable.componentRef.current; }, [dataTable.componentRef]);
    const reactToPrintTrigger = useCallback(() => { return <Dropdown.Item className="d-inline-flex align-items-center gap-1"><ZIcon icon={ZIconType.Print} /> Print</Dropdown.Item>; }, []);

    const [quickAddValue, setQuickAddValue] = useState<string>("");

    function handleAddQuickName(value: string) {
        dataTable.onQuickAdd(value);
        setQuickAddValue("");
    }

    const handleQuickAddKeyPress = (event: any) => {
        if (event.key === "Enter") {
            handleAddQuickName(quickAddValue);
            event.preventDefault();
        }
    };

    const TopbarChild = () => {
        return dataTable.topbarChild ? dataTable.topbarChild : null;
    };

    return (
        <Row className="mb-2 mt-1 d-flex align-items-center">
            <Col xs={12} md={12} lg={12} xl={4}>
                {isSearchable &&
                    <div className="d-flex main-search">
                        <GlobalFilter
                            searchColumns={searchColumns}
                            preGlobalFilteredRows={dataTable.preGlobalFilteredRows}
                            globalFilter={dataTable.state.globalFilter}
                            setGlobalFilter={dataTable.setGlobalFilter}
                            onSearchClick={dataTable.onSearchClick}
                        />
                         {dataTable.filterComponent && dataTable.filterComponent}
                    </div>
                }
                {dataTable.allowQuickAdd &&
                    <div className="d-flex align-items-left ">
                        <input
                            maxLength={100}
                            value={quickAddValue || ""}
                            onChange={(e) => setQuickAddValue(e.target.value)}
                            onKeyDown={handleQuickAddKeyPress}
                            placeholder={`Add new...`}
                            className="form-control w-auto"
                            style={{ minHeight: "38px", fontSize: 12, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                        />
                        <Button variant="primary" className="btn-icon me-1 custom-btn-icon filter-btn" style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }} onClick={() => handleAddQuickName(quickAddValue)}>Add</Button>
                    </div>
                }
                {dataTable.title && <h5 className="mb-0 fw-bold">{dataTable.title}</h5>}
            </Col>

            <Col xs={12} md={12} lg={12} xl={8} >
                <div className="d-flex table-toolbar">
                    <div className="d-flex justify-content-end mt-1 mt-xl-0 gap-1" style={{width: "100%"}}>

                    {/* Topbar Selected Count */}
                    {dataTable.isSelectable !== false && selectedRowCount > 0 && 
                        <Button disabled variant="light" className="btn-icon custom-btn-icon ">{selectedRowCount}  Selected</Button>                        
                    }

                    {/* Topbar Child */}
                    {dataTable.topbarChild !== null && <TopbarChild />}

                    {/* Add */}
                    {dataTable.addNewRow !== false &&
                        <ZTooltip text="Add">
                            <ZButton 
                                size="sm"
                                className="btn-icon custom-btn-icon"
                                onClick={dataTable.addNewRow} 
                                icon={ZIconType.Add}
                                iconSize="18px"
                            />
                        </ZTooltip>
                    }
                    
                    {/* Delete */}
                    {dataTable.isSelectable && dataTable.canDelete && <>
                        {dataTable.removeRows !== null &&
                            <ZTooltip text={dataTable.selectedFlatRows.length > 0 ? `Delete ${dataTable.selectedFlatRows.length} rows` : "Please select a row to delete"}>
                                <ZButton 
                                    variant="light" 
                                    disabled={dataTable.selectedFlatRows.length > 0 ? false : true} 
                                    onClick={dataTable.handleDelete} 
                                    className="btn-icon custom-btn-icon"
                                    icon={ZIconType.Delete}
                                    iconSize="18px"
                                />
                            </ZTooltip>
                        }
                    </>}

                    {/* Reload */}
                    {dataTable.reloadTableRows !== false &&
                        <ZTooltip text="Refresh">
                            <ZButton 
                                variant="light"
                                className="btn-icon custom-btn-icon" 
                                onClick={dataTable.reloadTableRows}
                                icon={ZIconType.Refresh}
                                iconSize="18px"
                            />
                        </ZTooltip>
                    }

                    {/* Cloud Sync */}
                    {dataTable.syncOptions !== false &&
                        <ZTooltip text="Sync">
                            <ZButton 
                                variant="light" 
                                size="sm" 
                                className="btn-icon custom-btn-icon" 
                                onClick={dataTable.syncOptions} 
                                icon={ZIconType.Sync} 
                                iconSize="18px"
                            />
                        </ZTooltip>
                    }

                    {/* Import */}
                    {dataTable.singleImport !== false &&
                        <ZTooltip text="Import">
                            <ZButton
                                size="sm"
                                className="btn-icon custom-btn-icon"
                                onClick={dataTable.singleImport} 
                                icon={ZIconType.Upload}
                                iconSize="18px"
                            />
                        </ZTooltip>
                    }

                    {/* Import Options */}
                    {dataTable.importProducts !== false && <Button variant="light" className="btn-icon d-inline-flex" onClick={dataTable.importProducts}><i className="uil-box" /> Product List</Button>}

                    {/* Export */}
                    {disableExport !== true && <>
                            <Dropdown>
                                <ZTooltip text="Export">
                                    <Dropdown.Toggle variant="light" id="dropdown-basic" className="btn-icon custom-btn-icon">
                                        <ZIcon icon={ZIconType.Download} size="18px" />
                                    </Dropdown.Toggle>
                                </ZTooltip>
                                <Dropdown.Menu className="w-auto">
                                    <Dropdown.Item className="d-inline-flex align-items-center gap-1" onClick={() => { dataTable.exportData("csv", true) }}><ZIcon icon={ZIconType.File} /> CSV File</Dropdown.Item>
                                    <Dropdown.Item className="d-inline-flex align-items-center gap-1" onClick={() => { dataTable.exportData("xlsx", true) }}><ZIcon icon={ZIconType.Excel} /> Excel File</Dropdown.Item>
                                    <Dropdown.Item className="d-inline-flex align-items-center gap-1" onClick={() => { dataTable.exportData("pdf", true) }}><ZIcon icon={ZIconType.PDF} /> PDF File</Dropdown.Item>
                                    <ReactToPrint content={reactToPrintContent} trigger={reactToPrintTrigger} />
                                </Dropdown.Menu>
                            </Dropdown>
                    </>}
                    </div>
                </div>
            </Col>
        </Row>
    );
}
